/* eslint-disable */

import 'App.css';
import { Box, Container, Typography, Link, Paper, Stack, Button } from '@mui/material';
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

const WelcomeCard = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgb(225, 190, 231)',
  padding: theme.spacing(1),
  borderRadius: 16,
  boxShadow: 'none',
  textAlign: 'center',
  background: 'rgb(225, 190, 231)',
  border: 'none',
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(4),
  borderRadius: 12,
  boxShadow: '0 2px 12px rgba(90, 74, 66, 0.08)',
  height: '100%',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 4px 20px rgba(90, 74, 66, 0.15)',
  },
}));

function HomePage() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');

  const checkAuthState = () => {
    const token = localStorage.getItem('token');
    const userEmail = localStorage.getItem('email');
    const storedName = localStorage.getItem('name');
    
    if (token && userEmail) {
      setAuthenticated(true);
      setEmail(userEmail);
      
      // Use stored name if available
      if (storedName) {
        setUserName(storedName);
      } else {
        setUserName('');
      }
    } else {
      setAuthenticated(false);
      setEmail('');
      setUserName('');
    }
  };

  useEffect(() => {
    // Check auth state on mount
    checkAuthState();

    // Listen for custom auth-change event (when user logs out/in)
    const handleAuthChange = () => {
      checkAuthState();
    };

    // Listen for storage changes (when user logs out in another tab/window)
    const handleStorageChange = (e) => {
      if (e.key === 'token' || e.key === 'email' || e.key === 'name') {
        checkAuthState();
      }
    };

    window.addEventListener('auth-change', handleAuthChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('auth-change', handleAuthChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, rgb(225, 190, 231) 0%, #F6F1E7 100%)',
      py: 8
    }}>
      <Container maxWidth="lg">
        <Stack spacing={6}>
          {/* Welcome Section */}
          <WelcomeCard>
            <Typography 
              variant="h1" 
              sx={{ 
                color: '#5A4A42',
                fontWeight: 400,
                mb: { xs: 0.5, md: 1 },
                mt: { xs: -3, md: -3, lg: -3 },
                fontSize: { xs: '2rem', md: '3rem' },
                fontFamily: '"Lobster", cursive',
                letterSpacing: '0.02em',
                lineHeight: 1.2,
                textShadow: '2px 2px 4px rgba(90, 74, 66, 0.1)',
                display: authenticated ? 'none' : 'block'
              }}
            >
              Welcome to
            </Typography>
            <Box 
              component="img" 
              src="/welcome-photo.png"
              alt="Los Knits Studio"
              sx={{
                maxWidth: '100%',
                height: 'auto',
                mb: { xs: 1, md: 1.5 },
                maxHeight: { xs: '200px', md: '300px', lg: '500px' },
                objectFit: 'contain'
              }}
            />
            <Typography 
              variant="h1" 
              sx={{ 
                color: '#5A4A42',
                fontWeight: 400,
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
                fontFamily: '"Lobster", cursive',
                letterSpacing: '0.02em',
                lineHeight: 1.2,
                textShadow: '2px 2px 4px rgba(90, 74, 66, 0.1)',
                display: authenticated ? 'block' : 'none'
              }}
            >
              Welcome back, {userName || email.split('@')[0]}!
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#5A4A42',
                fontWeight: 400,
                mb: 4,
                fontStyle: 'italic',
                opacity: 0.8
              }}
            >
              {authenticated 
                ? 'Ready to create your next masterpiece?'
                : 'Your design studio for custom patterns'
              }
            </Typography>
          </WelcomeCard>

          {/* Features Grid */}
          {!authenticated && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <FeatureCard>
              <Typography variant="h5" sx={{ color: '#5A4A42', fontWeight: 600, mb: 2 }}>
                🎨 Design
              </Typography>
              <Typography variant="body1" sx={{ color: '#5A4A42', lineHeight: 1.8 }}>
                Create intricate color charts and scale your designs to any size without test knitters. 
                Perfect for intarsia and fair isle. Design something truly unique that reflects your 
                personal style and fashion sense.
              </Typography>
            </FeatureCard>

            <FeatureCard>
              <Typography variant="h5" sx={{ color: '#5A4A42', fontWeight: 600, mb: 2 }}>
                🧶 Organize Your Stash
              </Typography>
              <Typography variant="body1" sx={{ color: '#5A4A42', lineHeight: 1.8 }}>
                Keep track of your yarn inventory and knitting tools. Never lose track of what you have 
                or what you need for your next project.
              </Typography>
            </FeatureCard>

            <FeatureCard>
              <Typography variant="h5" sx={{ color: '#5A4A42', fontWeight: 600, mb: 2 }}>
                📐 Precision Tools
              </Typography>
              <Typography variant="body1" sx={{ color: '#5A4A42', lineHeight: 1.8 }}>
                Professional-grade tools for serious knitters. Track gauge, needle sizes, and hook sizes 
                to ensure your projects turn out exactly as planned.
              </Typography>
            </FeatureCard>
          </Stack>
          )}

          {/* Footer Section */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link 
                href="https://www.instagram.com/losknits.studio/" 
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  color: '#C75D7B',
                  textDecoration: 'none',
                  borderBottom: '2px solid transparent',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderBottomColor: '#5A4A42'
                  }
                }}
              >
                Los Knits Studio on Instagram
              </Link>
              <Link 
                href="https://www.tiktok.com/@losknits.studio" 
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  color: '#C75D7B',
                  textDecoration: 'none',
                  borderBottom: '2px solid transparent',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderBottomColor: '#5A4A42'
                  }
                }}
              >
                Los Knits Studio on TikTok
              </Link>
            </Box><br />
            <Typography variant="body1" sx={{ color: '#5A4A42', mb: 2, fontStyle: 'italic' }}>
              Created with love by Audrey Sullivan<br />
              <Link 
                href="https://www.instagram.com/losknits/" 
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  color: '#C75D7B',
                  textDecoration: 'none',
                  borderBottom: '2px solid transparent',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderBottomColor: '#5A4A42'
                  }
                }}
              >
                @losknits &nbsp;|&nbsp;
              </Link>
              <Link 
                href="https://www.ravelry.com/designers/los-knits" 
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  color: '#C75D7B',
                  textDecoration: 'none',
                  borderBottom: '2px solid transparent',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderBottomColor: '#5A4A42'
                  }
                }}
              >
                losknits on Ravelry
              </Link>
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

export default HomePage
